import { useQuery } from "@apollo/client";
import { gql } from "generated-graphql";
import { ContainerFindingKind, FindingEntity } from "generated-graphql/graphql";
import { useTenant } from "hooks/useTenant";
import { useMemo } from "react";

/**
 * Findings for one facility, narrowed to the kinds a given surface cares about.
 *
 * Scoped by facility + kind rather than fetched tenant-wide: a tenant can carry
 * a great many findings, and an entity badge only ever needs its own record's.
 * There is deliberately no per-container or per-stream resolver field — bounding
 * this query is cheaper than a third resolver.
 */
const ENTITY_COMPLIANCE_FINDINGS_QUERY = gql(`
  query EntityComplianceFindings(
    $tenantId: ID!
    $facilityId: ID
    $kinds: [ContainerFindingKind!]
    $pageSize: Int
  ) {
    tenant(id: $tenantId) {
      id
      containerComplianceFindings(facilityId: $facilityId, kinds: $kinds, pageSize: $pageSize) {
        items {
          id
          kind
          severity
          entity
          entityLabel
          containerId
          areaId
          wasteStreamId
          title
          detail
          deadline
        }
      }
    }
  }
`);

/** Matches MAX_PAGE_SIZE on the resolver; a larger request is clamped there. */
const MAX_PAGE_SIZE = 50;

export const ACCUMULATION_KINDS = [
  ContainerFindingKind.AccumulationOverdue,
  ContainerFindingKind.AccumulationApproaching,
];

export const PROFILE_KINDS = [
  ContainerFindingKind.ProfileRetestOverdue,
  ContainerFindingKind.ProfileIncomplete,
];

export const FACILITY_CAP_KINDS = [
  ContainerFindingKind.OnsiteCapExceeded,
  ContainerFindingKind.OnsiteCapApproaching,
];

type Options = {
  facilityId: string | null | undefined;
  kinds: ContainerFindingKind[];
  /**
   * Callers behind an already-gated route can pass true; the stream drawer has to
   * pass its own flag check so an un-flagged tenant never issues the query.
   */
  enabled?: boolean;
};

/**
 * Returns the matching findings, plus narrowing helpers for the record a surface
 * is rendering. Empty (never undefined) so callers can render unconditionally.
 */
export function useEntityComplianceFindings({ facilityId, kinds, enabled = true }: Options) {
  const { tenantId } = useTenant();

  const { data } = useQuery(ENTITY_COMPLIANCE_FINDINGS_QUERY, {
    // Asks for the server's maximum: the callers narrow to one record client-side,
    // so a page that cut off would silently drop that record's badge.
    variables: { tenantId: tenantId ?? "", facilityId, kinds, pageSize: MAX_PAGE_SIZE },
    skip: !enabled || !tenantId || !facilityId,
    // Findings derive at read, so a revisit should reflect a fix made elsewhere.
    fetchPolicy: "cache-and-network",
  });

  const findings = useMemo(() => data?.tenant?.containerComplianceFindings.items ?? [], [data]);

  return useMemo(
    () => ({
      findings,
      forContainer: (containerId: string) => findings.filter((f) => f.containerId === containerId),
      forWasteStream: (wasteStreamId: string) =>
        findings.filter((f) => f.wasteStreamId === wasteStreamId),
      forFacility: () => findings.filter((f) => f.entity === FindingEntity.Facility),
    }),
    [findings]
  );
}
